import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class SeoService {

  private readonly empresa = environment.name;

  private readonly defaultTitle = 'Compra pasajes en micro al mejor precio';
  private readonly defaultDescription =
    `Viaja con ${this.empresa}. Pasajes de bus al mejor precio 100% online.`;

  constructor(
    private meta: Meta,
    private title: Title,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  init(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => this.updateMetaTags(event.urlAfterRedirects));
  }

  private updateMetaTags(url: string): void {
    const segments = url.split('?')[0].split('/').filter(Boolean);

    if (segments[0] === 'buscar' && segments.length >= 3) {
      const origen = this.seoNameToLabel(segments[1]);
      const destino = this.seoNameToLabel(segments[2]);

      this.title.setTitle(
        `Compra pasajes en micro de ${origen} a ${destino} al mejor precio`
      );
      this.meta.updateTag({
        name: 'description',
        content: `Viaja de ${origen} a ${destino} con ${this.empresa}. Pasajes de bus al mejor precio 100% online.`
      });
      this.meta.updateTag({ name: 'robots', content: 'index, follow' });
      this.setCanonicalFromLocation();
    } else {
      this.title.setTitle(this.defaultTitle);
      this.meta.updateTag({ name: 'description', content: this.defaultDescription });
      this.meta.updateTag({ name: 'robots', content: 'noindex' });
      this.removeCanonicalLink();
    }
  }

  /** URL absoluta sin query ni fragmento; refleja pathname público (p. ej. prefijo /buscar-pasajes/). */
  private setCanonicalFromLocation(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const loc = this.document.defaultView?.location;
    if (!loc) {
      return;
    }
    const href = `${loc.origin}${loc.pathname}`;
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private removeCanonicalLink(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.document.querySelector('link[rel="canonical"]')?.remove();
  }

  private seoNameToLabel(seoName: string): string {
    return seoName
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
